import React from 'react';
import { TrendingUp, Calendar, Target, Sparkles } from 'lucide-react';
import { Card } from './ui/card';

const InsightsCard = ({ habits, completions }) => {
  const getBestDayOfWeek = () => {
    if (completions.length === 0) return null;

    const dayCompletions = {};
    const dayNames = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];

    completions.forEach(comp => {
      const day = new Date(comp.date).getDay();
      dayCompletions[day] = (dayCompletions[day] || 0) + 1;
    });

    const bestDay = Object.keys(dayCompletions).reduce((a, b) =>
      dayCompletions[a] > dayCompletions[b] ? a : b
    );

    return dayNames[bestDay];
  };



  // Helper to calculate completion efficiency for a date range
  const calculateEfficiency = (startDaysAgo, endDaysAgo) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    let totalPotential = 0;
    let totalActual = 0;

    for (let i = startDaysAgo; i >= endDaysAgo; i--) {
      const d = new Date(today);
      d.setDate(d.getDate() - i);
      const dStr = d.toISOString().split('T')[0];

      const activeHabits = habits.filter(h => {
        const creationTime = h.created_at || h.createdAt;
        if (!creationTime) return true;
        const createdDate = new Date(creationTime);
        createdDate.setHours(0, 0, 0, 0);
        return createdDate <= d;
      });

      totalPotential += activeHabits.length;

      activeHabits.forEach(h => {
        const comp = completions.find(c => c.habitId === h.id && c.date === dStr);
        if (comp) {
          let score = 1;
          if (h.target && comp.value) {
            score = Math.min(parseFloat(comp.value) / parseFloat(h.target), 1);
          }
          totalActual += score;
        }
      });
    }

    return totalPotential === 0 ? 0 : (totalActual / totalPotential) * 100;
  };

  const currentEfficiency = calculateEfficiency(6, 0); // Last 7 days
  const previousEfficiency = calculateEfficiency(13, 7); // Previous 7 days

  const trend = currentEfficiency > previousEfficiency ? 'up' :
    currentEfficiency < previousEfficiency ? 'down' : 'stable';

  const isTrendSame = Math.abs(currentEfficiency - previousEfficiency) < 1;
  const trendLabel = isTrendSame ? 'Estável' : (trend === 'up' ? 'Melhorando' : 'Em queda');

  const bestDay = getBestDayOfWeek();

  if (completions.length === 0 && habits.length === 0) {
    return null;
  }

  return (
    <Card className="p-5 bg-gradient-to-br from-blue-50/50 via-indigo-50/30 to-purple-50/50 dark:from-blue-950/20 dark:via-indigo-950/20 dark:to-purple-950/20 border-blue-200/50 dark:border-blue-800/30">
      <div className="flex items-center gap-2 mb-4">
        <div className="p-1.5 rounded-md bg-blue-100 dark:bg-blue-900/50">
          <Sparkles className="w-4 h-4 text-blue-600 dark:text-blue-400" />
        </div>
        <h3 className="font-semibold text-foreground">Insights</h3>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
        {bestDay && (
          <div className="flex items-start gap-3 p-3 rounded-lg bg-white/60 dark:bg-white/5 border border-border/50">
            <div className="flex-shrink-0 p-1.5 rounded-md bg-emerald-100 dark:bg-emerald-900/50">
              <Calendar className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-muted-foreground mb-0.5">Melhor dia</p>
              <p className="font-semibold text-foreground">{bestDay}</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">Dia com mais atividade</p>
            </div>
          </div>
        )}

        <div className="flex items-start gap-3 p-3 rounded-lg bg-white/60 dark:bg-white/5 border border-border/50">
          <div className="flex-shrink-0 p-1.5 rounded-md bg-blue-100 dark:bg-blue-900/50">
            <Target className="w-4 h-4 text-blue-600 dark:text-blue-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-muted-foreground mb-0.5">Consistência (7d)</p>
            <p className="font-semibold text-foreground">{Math.round(currentEfficiency)}%</p>
            <p className="text-[10px] text-muted-foreground mt-0.5">Média de conclusão</p>
          </div>
        </div>

        <div className="flex items-start gap-3 p-3 rounded-lg bg-white/60 dark:bg-white/5 border border-border/50">
          <div className="flex-shrink-0 p-1.5 rounded-md bg-purple-100 dark:bg-purple-900/50">
            <TrendingUp className="w-4 h-4 text-purple-600 dark:text-purple-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-muted-foreground mb-0.5">Tendência</p>
            <p className="font-semibold text-foreground">{trendLabel}</p>
            <p className="text-[10px] text-muted-foreground mt-0.5">Vs. semana anterior</p>
          </div>
        </div>
      </div>

    </Card>
  );
};

export default InsightsCard;
